import { Controller } from "@hotwired/stimulus"

// "Pick from Photos" pop-up: the project's photos (the Photos page's own
// project_album list, so it shows exactly what this person may see) in a grid
// with a tick box on each. "Add selected" hands the ticked ones back as a
// `photo-picker:picked` event ({ photos: [{ id, filename }] }); the page
// decides what to do with them. First used by the contractor pay request
// upload box (Larry Daleo, Quality Plumbing, Intercom 2026-11-03).
export default class extends Controller {
  static targets = ["modal", "grid", "empty", "loading", "loadMore", "addBtn", "count"]
  static values = { url: String }

  connect() {
    this._reset()
  }

  disconnect() {
    this._stopEscape()
  }

  open() {
    this._reset()
    this.gridTarget.innerHTML = ""
    this.emptyTarget.classList.add("hidden")
    this.loadMoreTarget.classList.add("hidden")
    this._updateCount()
    this.modalTarget.classList.remove("hidden")
    // Capture phase so Escape closes only this pop-up, not the pay request under it.
    this._escHandler = (e) => {
      if (e.key !== "Escape") return
      e.stopPropagation()
      this.close()
    }
    document.addEventListener("keydown", this._escHandler, true)
    this._loadPage()
  }

  close() {
    this.modalTarget.classList.add("hidden")
    this._stopEscape()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  loadMore() {
    this._loadPage()
  }

  add() {
    if (this._selected.size === 0) return
    const photos = Array.from(this._selected.values())
    this.close()
    this.dispatch("picked", { detail: { photos } })
  }

  _reset() {
    this._selected = new Map()
    this._page = 0
    this._lastPage = false
    this._loading = false
  }

  _stopEscape() {
    if (this._escHandler) document.removeEventListener("keydown", this._escHandler, true)
    this._escHandler = null
  }

  async _loadPage() {
    if (this._loading || this._lastPage) return
    this._loading = true
    this.loadingTarget.classList.remove("hidden")
    this.loadMoreTarget.classList.add("hidden")
    try {
      const page = this._page + 1
      const res = await fetch(`${this.urlValue}?page=${page}`, { headers: { Accept: "application/json" } })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      this._page = page
      this._lastPage = !!data.is_last_page
      ;(data.photos || [])
        .filter(p => p.is_image && !p.is_video && p.thumbnail_url)
        .forEach(p => this.gridTarget.appendChild(this._tile(p)))
    } catch (e) {
      console.error("Photo picker failed to load:", e)
      this._lastPage = true
    } finally {
      this._loading = false
      this.loadingTarget.classList.add("hidden")
      this.loadMoreTarget.classList.toggle("hidden", this._lastPage)
      this.emptyTarget.classList.toggle("hidden", !this._lastPage || this.gridTarget.children.length > 0)
    }
  }

  _tile(photo) {
    const tile = document.createElement("label")
    tile.className = "relative block aspect-square rounded-md overflow-hidden border-2 border-transparent cursor-pointer bg-gray-100"
    if (photo.comment_text) tile.title = photo.comment_text

    const img = document.createElement("img")
    img.src = photo.thumbnail_url
    img.loading = "lazy"
    img.alt = photo.filename || ""
    img.className = "w-full h-full object-cover"

    const box = document.createElement("input")
    box.type = "checkbox"
    box.className = "absolute top-1.5 left-1.5 w-4 h-4 cursor-pointer accent-brand"
    box.addEventListener("change", () => {
      if (box.checked) {
        this._selected.set(photo.id, { id: photo.id, filename: photo.filename || "" })
      } else {
        this._selected.delete(photo.id)
      }
      tile.classList.toggle("border-brand", box.checked)
      tile.classList.toggle("border-transparent", !box.checked)
      this._updateCount()
    })

    tile.append(img, box)
    return tile
  }

  _updateCount() {
    const n = this._selected.size
    this.countTarget.textContent = n
    this.addBtnTarget.disabled = n === 0
    this.addBtnTarget.classList.toggle("opacity-50", n === 0)
    this.addBtnTarget.classList.toggle("cursor-not-allowed", n === 0)
  }
}
